import { Injectable } from '@angular/core';
import { Router, NavigationStart, NavigationEnd, NavigationCancel, NavigationError } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { EventBusService } from './event-bus.service';

@Injectable({ providedIn: 'root' })
export class NavigationLoaderService {
  private isNavigating = false;
  private pendingApiRequests = 0;
  private loaderTimeout: any;
  private readonly LOADER_TIMEOUT = 30000; // 30 seconds timeout
  
  // Use a subject to communicate with the global loader
  private globalLoaderSubject = new BehaviorSubject<boolean>(false);

  constructor(
    private router: Router,
    private eventBus: EventBusService
  ) {
    this.setupRouterEvents();
  }

  private setupRouterEvents() {
    // Show loader on navigation start
    this.router.events.pipe(
      filter(event => event instanceof NavigationStart)
    ).subscribe(() => {
      this.isNavigating = true;
      this.globalLoaderSubject.next(true);
      this.eventBus.setNavigationLoading(true);
    });

    // Hide loader on navigation end/cancel/error
    this.router.events.pipe(
      filter(event => 
        event instanceof NavigationEnd || 
        event instanceof NavigationCancel || 
        event instanceof NavigationError
      )
    ).subscribe((event) => {
      this.isNavigating = false;
      
      // If navigation was cancelled or had an error, force hide loader
      if (event instanceof NavigationCancel || event instanceof NavigationError) {
        this.forceHideLoader();
        return;
      }
      
      // Only hide loader if no pending API requests
      if (this.pendingApiRequests === 0) {
        this.globalLoaderSubject.next(false);
        this.eventBus.setNavigationLoading(false);
      }
    });
  }

  /**
   * Start navigation loading - called immediately when sidebar is clicked
   */
  startNavigationLoading() {
    this.isNavigating = true;
    this.globalLoaderSubject.next(true);
    this.eventBus.setNavigationLoading(true);
    
    // Set a timeout to force hide loader if it takes too long
    this.clearLoaderTimeout();
    this.loaderTimeout = setTimeout(() => {
      console.warn('Navigation loader timeout - forcing hide');
      this.forceHideLoader();
    }, this.LOADER_TIMEOUT);
  }

  /**
   * Increment pending API requests counter
   */
  incrementPendingRequests() {
    this.pendingApiRequests++;
  }

  /**
   * Decrement pending API requests counter and hide loader if all done
   */
  decrementPendingRequests() {
    this.pendingApiRequests = Math.max(0, this.pendingApiRequests - 1);
    
    // Hide loader if navigation is complete and no pending requests
    if (!this.isNavigating && this.pendingApiRequests === 0) {
      this.globalLoaderSubject.next(false);
      this.eventBus.setNavigationLoading(false);
      this.clearLoaderTimeout();
    }
  }

  /**
   * Force hide loader (for error cases)
   */
  forceHideLoader() {
    this.isNavigating = false;
    this.pendingApiRequests = 0;
    this.globalLoaderSubject.next(false);
    this.eventBus.setNavigationLoading(false);
    this.clearLoaderTimeout();
  }

  /**
   * Clear the loader timeout
   */
  private clearLoaderTimeout() {
    if (this.loaderTimeout) {
      clearTimeout(this.loaderTimeout);
      this.loaderTimeout = null;
    }
  }

  /**
   * Check if currently navigating
   */
  isCurrentlyNavigating(): boolean {
    return this.isNavigating;
  }

  /**
   * Get pending requests count
   */
  getPendingRequestsCount(): number {
    return this.pendingApiRequests;
  }

  /**
   * Get the global loader observable
   */
  getGlobalLoaderObservable() {
    return this.globalLoaderSubject.asObservable();
  }


}
